import type { AuthoredSonicsAction } from "./sonics.actions";
import type { AuthoredSonicsState } from "./sonics.types";

export const DEFAULT_AUTHORED_SONICS_STATE: AuthoredSonicsState = {
  emotionalIntentSegmentsById: {},
  emotionalIntentOrder: [],
  fxIntentNodesById: {},
  fxIntentOrder: [],
};

export function authoredSonicsReducer(
  state: AuthoredSonicsState = DEFAULT_AUTHORED_SONICS_STATE,
  action: AuthoredSonicsAction,
): AuthoredSonicsState {
  switch (action.type) {
    case "sonics/emotion/upsert": {
      const exists = !!state.emotionalIntentSegmentsById[action.segment.id];
      return {
        ...state,
        emotionalIntentSegmentsById: {
          ...state.emotionalIntentSegmentsById,
          [action.segment.id]: action.segment,
        },
        emotionalIntentOrder: exists
          ? state.emotionalIntentOrder
          : [...state.emotionalIntentOrder, action.segment.id],
      };
    }

    case "sonics/emotion/upsertMany": {
      const nextById = { ...state.emotionalIntentSegmentsById };
      const nextOrder = [...state.emotionalIntentOrder];

      for (const segment of action.segments) {
        const exists = !!nextById[segment.id];
        nextById[segment.id] = segment;
        if (!exists) nextOrder.push(segment.id);
      }

      return {
        ...state,
        emotionalIntentSegmentsById: nextById,
        emotionalIntentOrder: nextOrder,
      };
    }

    case "sonics/emotion/remove": {
      const nextById = { ...state.emotionalIntentSegmentsById };
      delete nextById[action.segmentId];

      return {
        ...state,
        emotionalIntentSegmentsById: nextById,
        emotionalIntentOrder: state.emotionalIntentOrder.filter((id) => id !== action.segmentId),
      };
    }

    case "sonics/emotion/reorder":
      return {
        ...state,
        emotionalIntentOrder: action.order,
      };

    case "sonics/fx/upsert": {
      const exists = !!state.fxIntentNodesById[action.node.id];
      return {
        ...state,
        fxIntentNodesById: {
          ...state.fxIntentNodesById,
          [action.node.id]: action.node,
        },
        fxIntentOrder: exists
          ? state.fxIntentOrder
          : [...state.fxIntentOrder, action.node.id],
      };
    }

    case "sonics/fx/upsertMany": {
      const nextById = { ...state.fxIntentNodesById };
      const nextOrder = [...state.fxIntentOrder];

      for (const node of action.nodes) {
        const exists = !!nextById[node.id];
        nextById[node.id] = node;
        if (!exists) nextOrder.push(node.id);
      }

      return {
        ...state,
        fxIntentNodesById: nextById,
        fxIntentOrder: nextOrder,
      };
    }

    case "sonics/fx/remove": {
      const nextById = { ...state.fxIntentNodesById };
      delete nextById[action.nodeId];

      return {
        ...state,
        fxIntentNodesById: nextById,
        fxIntentOrder: state.fxIntentOrder.filter((id) => id !== action.nodeId),
      };
    }

    case "sonics/fx/reorder":
      return {
        ...state,
        fxIntentOrder: action.order,
      };

    case "sonics/migrate/fromShell": {
      const emotionalSegments = action.payload.emotionalSegments ?? [];
      const fxNodes = action.payload.fxNodes ?? [];

      let nextState = state;

      if (emotionalSegments.length > 0) {
        nextState = authoredSonicsReducer(nextState, {
          type: "sonics/emotion/upsertMany",
          segments: emotionalSegments,
        });
      }

      if (fxNodes.length > 0) {
        nextState = authoredSonicsReducer(nextState, {
          type: "sonics/fx/upsertMany",
          nodes: fxNodes,
        });
      }

      return nextState;
    }

    default:
      return state;
  }
}